import {useEffect} from 'react';
import {useHistory, useParams, useRouteMatch} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {translations} from '../../../../../../../i18n/translations';
import {useOffice} from '../../../../../../../contexts/office/context';
import {useSnackBar} from '../../../../../../../contexts/snack-bar/context';
import DeviceDetailsProfessionalCaregiverOfficeScreen from '../../../devices/edit/details/device-details.professional-caregiver.screen';
import {DevicesRepository} from '../../../../../../../repositories/devices';
import {useAuth} from '../../../../../../../contexts/auth/context';
import {RolePermissionType} from '../../../../../../../backend/enums/role-permission-type';
import {Box} from '@mui/material';
import GenericHeader from '../../../../../../../components/generic-header/generic-header';
import {ScreenPaths} from '../../../../../../../enums';
import '../../../../../../../components/devices-associations/devices-associations.scss';

export default function SeniorDeviceDetailsProfessionalCaregiverOfficeScreen() {
  let {deviceId, entityId, id}: {entityId: string; deviceId?: string; id: string} = useParams();
  if (deviceId?.toLowerCase() === 'create') {
    deviceId = undefined;
  }

  // The `path` lets us build <Route> paths that are
  // relative to the parent route, while the `url` lets
  // us build relative links.
  let {url} = useRouteMatch();
  let history = useHistory();
  const {t} = useTranslation();
  const officeContext = useOffice();
  const snackBarContext = useSnackBar();

  const {hasWritePermission} = useAuth();
  const canWrite = hasWritePermission(RolePermissionType.Seniors_DigitalInclusion_Devices);

  // Validate and get the device
  useEffect(() => {
    const getDevice = async () => {
      if (deviceId) {
        try {
          // Get device
          const result = await DevicesRepository.instance.getById(deviceId);

          if (result) {
            // Edit device
            officeContext.setDeviceForm(result);
          } else {
            // Error
            throw new Error('Empty device.');
          }
        } catch (error) {
          // Log
          console.error('🔴 Could not get device.', error);

          // Show toast error
          snackBarContext.setOfficeToastNotification({
            severity: 'error',
            message: t(translations.device_tabs.errors.device_does_not_exist_error),
          });

          // Redirect
          history.push(`${url.replace(`/${deviceId}`, '')}`);
        }
      }
    };

    // Get device
    getDevice();

    // Clean up
    return () => {
      // Clear device
      officeContext.clearDeviceForm();

      // Log
      console.debug('🧹 Device form cleaned in office context.');
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId]);

  return (
    <Box display="flex" flexDirection="column" flexGrow={1}>
      <GenericHeader
        titleCrumbs={[
          {
            label: t(translations.screen_paths.professional_caregiver_seniors),
            link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_Seniors}`,
          },
          {
            label:
              (officeContext.state.userForm?.user.displayName ||
                officeContext.state.userForm?.user.name)! || '',
            link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_Seniors}/${id}${ScreenPaths.Senior_PersonalData}${ScreenPaths.Senior_Profile}`,
          },
          {
            label: t(translations.role_permissions_section['digital-inclusion']),
            link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_Seniors}/${id}${ScreenPaths.Senior_DigitalInclusion}`,
          },
          {
            label: t(translations.screen_paths.senior_devices),
            link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_Seniors}/${id}${ScreenPaths.Senior_DigitalInclusion}${ScreenPaths.Senior_Devices}`,
          },
          {
            label:
              officeContext.state.deviceForm?.serialNumber ||
              officeContext.state.deviceForm?.macAddress,
            link: url,
          },
        ]}
        isLoading={false}
        leftColumn={<></>}
        rightColumn={<></>}
      />
      <Box className="devices-listing-container">
        <Box className="devices-listing-container-centered">
          <DeviceDetailsProfessionalCaregiverOfficeScreen canWrite={canWrite ?? false} />
        </Box>
      </Box>
    </Box>
  );
}
